import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from '@/shared/api';
import { reconnectSocket } from '@/shared/socket';
import { rememberTicket, rememberedTicket } from '@/shared/web-login-ticket';
import { useSessionStore } from '@/store/session.store';

/**
 * Вход через бота в веб-версии.
 *
 * Человек уходит в Telegram и жмёт «Старт», а вкладка ждёт: сервер узнаёт об
 * этом сам, страницу никто не перезагружает. Одно нажатие закрывает и вход, и
 * право бота присылать напоминания — Login Widget дал бы только первое.
 */

/** Как часто спрашивать сервер. Реже — заметная задержка, чаще — лишний шум. */
const POLL_INTERVAL_MS = 3000;

export type BotLoginState = 'idle' | 'starting' | 'waiting' | 'expired' | 'failed';

export interface BotLogin {
  state: BotLoginState;
  /** Ссылка на бота: пригодится, если браузер не дал открыть вкладку. */
  url: string | null;
  begin: () => void;
  cancel: () => void;
}

export function useBotLogin(onSuccess: () => void): BotLogin {
  const [state, setState] = useState<BotLoginState>('idle');
  const [url, setUrl] = useState<string | null>(null);
  const adoptSession = useSessionStore((store) => store.adoptSession);

  const timer = useRef<number | null>(null);
  const succeed = useRef(onSuccess);
  succeed.current = onSuccess;

  const stop = useCallback(() => {
    if (timer.current !== null) {
      window.clearInterval(timer.current);
      timer.current = null;
    }
  }, []);

  useEffect(() => stop, [stop]);

  /** Ожидание «Старта»: опрос до сессии, отказа или отмены. */
  const watch = useCallback(
    (code: string) => {
      stop();
      timer.current = window.setInterval(() => {
        void api
          .webLoginStatus(code)
          .then((status) => {
            if (status.status !== 'ready') {
              return;
            }
            stop();
            rememberTicket(null);
            setState('idle');

            /*
              Сокет поднимается заново до того, как экран узнает о входе:
              токены уже сменились, а соединение всё ещё гостевое.
            */
            reconnectSocket();
            adoptSession(status.session.user);
            succeed.current();
          })
          .catch((error: unknown) => {
            // Заявка живёт минуты: пропала — значит, человек не успел.
            if (error instanceof ApiError && error.status === 404) {
              stop();
              rememberTicket(null);
              setState('expired');
            }
          });
      }, POLL_INTERVAL_MS);
    },
    [adoptSession, stop],
  );

  // Вкладку перезагрузили, пока человек был в Telegram: ждём его дальше.
  useEffect(() => {
    const ticket = rememberedTicket();
    if (ticket) {
      setUrl(ticket.url);
      setState('waiting');
      watch(ticket.code);
    }
  }, [watch]);

  const begin = useCallback(() => {
    /*
      Вкладка открывается сразу, ещё пустой, и только потом получает адрес:
      открыть её после ответа сервера уже нельзя — браузер сочтёт это
      всплывающим окном и заблокирует, потому что нажатие к тому моменту
      закончилось.
    */
    const tab = window.open('', '_blank');
    setState('starting');

    void api
      .startWebLogin()
      .then((ticket) => {
        setUrl(ticket.url);
        setState('waiting');
        rememberTicket({
          code: ticket.code,
          url: ticket.url,
          expiresAt: Date.now() + ticket.expiresIn * 1000,
        });

        if (tab) {
          tab.location.href = ticket.url;
        }

        watch(ticket.code);
      })
      .catch(() => {
        tab?.close();
        setState('failed');
      });
  }, [watch]);

  const cancel = useCallback(() => {
    stop();
    rememberTicket(null);
    setState('idle');
    setUrl(null);
  }, [stop]);

  return { state, url, begin, cancel };
}
